import {useCallback, useRef, useState} from 'react';
import {ENTRY_MANAGEMENTS_TODO} from '../../../config/constants/routes';
import {useCreateEntryTodo} from '../../../core/repositories/entryTodosRepository';
import {useIsAppWebView} from '../../../shared/hooks/useIsAppWebView';
import type {TodoCandidateType} from './useAiTodoGeneration';

type TodoSaveState = {
  savedIndices: Set<number>;
  savingIndex: number | null;
  isSaving: boolean;
  isSavingAll: boolean;
};

type UseAiTodoSaveArgs = {
  userEntryManagementId: number | null;
  /** PCかどうか（SPの場合はToDo一覧を開く前に確認モーダルを表示） */
  isPC: boolean;
};

export const useAiTodoSave = ({
  userEntryManagementId,
  isPC = true,
}: UseAiTodoSaveArgs) => {
  const isWebView = useIsAppWebView();
  const hasOpenedTodoPageRef = useRef(false);
  const [saveState, setSaveState] = useState<TodoSaveState>({
    savedIndices: new Set(),
    savingIndex: null,
    isSaving: false,
    isSavingAll: false,
  });

  // SP用: ToDo一覧を開く確認モーダルの状態
  const [showOpenTodoPageConfirm, setShowOpenTodoPageConfirm] = useState(false);
  const [todoPageUrl, setTodoPageUrl] = useState<string | null>(null);

  const createEntryTodo = useCreateEntryTodo();

  /**
   * 初回ToDo保存時にToDo一覧ページを開く処理
   *
   * @description
   * - PCの場合: 非同期処理後でもwindow.openが許可されるため、直接別タブで開く
   * - SPの場合: ポップアップブロッカーにより非同期処理後のwindow.openがブロックされるため、
   *             ユーザーの明示的なクリックアクション（確認モーダル経由）で開く必要がある
   * - webviewの場合: アプリ内ブラウザのため、別タブで開かない
   */
  const openTodoPageOnFirstSave = useCallback(
    (url: string) => {
      if (isPC) {
        // PCの場合は直接開く
        window.open(url, '_blank');
      } else {
        // SPの場合は確認モーダルを表示
        setTodoPageUrl(url);
        setShowOpenTodoPageConfirm(true);
      }
    },
    [isPC],
  );

  const saveSingleTodo = useCallback(
    async (
      candidate: TodoCandidateType,
      index: number,
      stepId: number,
    ): Promise<boolean> => {
      if (!stepId) return false;

      try {
        const result = await createEntryTodo.mutateAsync({
          userEntryManagementID: userEntryManagementId,
          userEntryManagementStepID: stepId,
          title: candidate.title,
          body: candidate.body,
          link: candidate.link,
          dueDate: candidate.dueDate ?? undefined,
          priority: candidate.priority,
          isAiGenerated: true,
        });

        if (result.ok) {
          // 初回保存時にToDoページを別タブで開く（webviewは除く）
          if (
            !hasOpenedTodoPageRef.current &&
            userEntryManagementId &&
            !isWebView
          ) {
            hasOpenedTodoPageRef.current = true;
            const url = `${ENTRY_MANAGEMENTS_TODO}?entry_management_id=${userEntryManagementId}`;
            openTodoPageOnFirstSave(url);
          }
          setSaveState((prev) => ({
            ...prev,
            savedIndices: new Set(prev.savedIndices).add(index),
          }));
          return true;
        }
        return false;
      } catch (error) {
        console.error('[Save ToDo] Error:', error);
        return false;
      }
    },
    [userEntryManagementId, createEntryTodo, openTodoPageOnFirstSave],
  );

  const handleSaveTodo = useCallback(
    async (
      candidate: TodoCandidateType,
      index: number,
      stepId: number,
      editingIndex: number | null,
      onEditComplete: () => void,
    ): Promise<boolean> => {
      setSaveState((prev) => ({
        ...prev,
        isSaving: true,
        savingIndex: index,
      }));

      const success = await saveSingleTodo(candidate, index, stepId);

      if (success && editingIndex === index) {
        onEditComplete();
      }

      setSaveState((prev) => ({
        ...prev,
        isSaving: false,
        savingIndex: null,
      }));

      return success;
    },
    [saveSingleTodo],
  );

  const handleSaveAllTodos = useCallback(
    async (candidates: TodoCandidateType[], stepId: number) => {
      if (!stepId) return;

      setSaveState((prev) => ({
        ...prev,
        isSavingAll: true,
      }));

      for (let i = 0; i < candidates.length; i++) {
        const candidate = candidates[i];
        if (!saveState.savedIndices.has(i) && candidate) {
          setSaveState((prev) => ({
            ...prev,
            savingIndex: i,
          }));
          await saveSingleTodo(candidate, i, stepId);
        }
      }

      setSaveState((prev) => ({
        ...prev,
        savingIndex: null,
        isSavingAll: false,
      }));
    },
    [saveState.savedIndices, saveSingleTodo],
  );

  const isTodoSaved = useCallback(
    (index: number) => saveState.savedIndices.has(index),
    [saveState.savedIndices],
  );

  const resetSaveState = useCallback(() => {
    hasOpenedTodoPageRef.current = false;
    setSaveState({
      savedIndices: new Set(),
      savingIndex: null,
      isSaving: false,
      isSavingAll: false,
    });
    setShowOpenTodoPageConfirm(false);
    setTodoPageUrl(null);
  }, []);

  // SP用: ToDo一覧を開く確認モーダルの処理
  const handleOpenTodoPageConfirm = useCallback(() => {
    if (todoPageUrl) {
      window.open(todoPageUrl, '_blank');
    }
    setShowOpenTodoPageConfirm(false);
    setTodoPageUrl(null);
  }, [todoPageUrl]);

  const handleCloseOpenTodoPageConfirm = useCallback(() => {
    setShowOpenTodoPageConfirm(false);
    setTodoPageUrl(null);
  }, []);

  return {
    hasOpenedTodoPageRef,
    saveState,
    handleSaveTodo,
    handleSaveAllTodos,
    isTodoSaved,
    resetSaveState,
    // SP用: ToDo一覧を開く確認モーダル
    showOpenTodoPageConfirm,
    handleOpenTodoPageConfirm,
    handleCloseOpenTodoPageConfirm,
  };
};
